
/**
 * Expands recurring events into instances within a given range.
 * @param {Array} events - List of event objects.
 * @param {Date} rangeStart - Start of the visible range.
 * @param {Date} rangeEnd - End of the visible range.
 * @returns {Array} - List of events including expanded instances.
 */
export const expandRecurringEvents = (events, rangeStart, rangeEnd) => {
    const expandedEvents = [];

    events.forEach(event => {
        const isTaskRecurrent = event.recurrent_interval && event.recurrent_interval > 0 && event.recurrent_unit;
        const isEventRecurrent = event.recurrence && event.recurrence.type !== 'none';

        // If not recurring, just add it if it overlaps
        if (!isTaskRecurrent && !isEventRecurrent) {
            if (event.end >= rangeStart && event.start <= rangeEnd) {
                expandedEvents.push(event);
            }
            return; // processed
        }

        // It is recurring. Generate instances.
        const rule = isEventRecurrent ? event.recurrence : null;
        const type = rule ? rule.type : null;

        // Start from event start time
        let currentStart = new Date(event.start);
        let currentEnd = new Date(event.end);
        const duration = currentEnd.getTime() - currentStart.getTime();

        // Safety: Prevent infinite loops if start > rangeEnd
        if (currentStart > rangeEnd) return;

        let count = 0;
        const LIMIT = 3650; // Increased to 10 years to prevent old recurrences from dropping off

        // Loop through occurrences
        while (currentStart <= rangeEnd && count < LIMIT) {
            // Check intersection: Is this instance visible?
            if (currentEnd >= rangeStart && currentStart <= rangeEnd) {
                const isoStart = currentStart.toISOString();
                const isException = isEventRecurrent && event.exceptions && Array.isArray(event.exceptions) && event.exceptions.includes(isoStart);

                if (!isException) {
                    expandedEvents.push({
                        ...event,
                        id: `${event.id}_${isoStart}`, // Virtual ID
                        start: new Date(currentStart),
                        end: new Date(currentEnd),
                        isRecurrenceInstance: true,
                        originalEventId: event.id,
                        originalStart: event.start, // Preserve original series start
                        originalEnd: event.end // Preserve original series end
                    });
                }
            }

            // Increment loop counter
            count++;

            // Increment based on type or interval
            const nextStart = new Date(currentStart);
            if (isTaskRecurrent) {
                const interval = parseInt(event.recurrent_interval, 10);
                if (event.recurrent_unit === 'dias') {
                    nextStart.setDate(nextStart.getDate() + interval);
                } else if (event.recurrent_unit === 'weeks' || event.recurrent_unit === 'semanas') {
                    nextStart.setDate(nextStart.getDate() + (interval * 7));
                } else if (event.recurrent_unit === 'meses') {
                    nextStart.setMonth(nextStart.getMonth() + interval);
                } else if (event.recurrent_unit === 'años') {
                    nextStart.setFullYear(nextStart.getFullYear() + interval);
                } else {
                    break; // Unknown unit
                }
            } else if (isEventRecurrent) {
                if (type === 'daily') {
                    nextStart.setDate(nextStart.getDate() + 1);
                } else if (type === 'weekly') {
                    if (rule.days && Array.isArray(rule.days) && rule.days.length > 0) {
                        // Find next valid day
                        let foundNext = false;
                        // Check next 7 days to find next occurrence
                        for (let i = 1; i <= 7; i++) {
                            const potentialDate = new Date(currentStart);
                            potentialDate.setDate(potentialDate.getDate() + i);
                            if (rule.days.includes(potentialDate.getDay())) {
                                nextStart.setDate(nextStart.getDate() + i);
                                foundNext = true;
                                break;
                            }
                        }
                        if (!foundNext) nextStart.setDate(nextStart.getDate() + 7);
                    } else {
                        nextStart.setDate(nextStart.getDate() + 7);
                    }
                } else if (type === 'monthly') {
                    nextStart.setMonth(nextStart.getMonth() + 1);
                } else if (type === 'yearly') {
                    nextStart.setFullYear(nextStart.getFullYear() + 1);
                } else {
                    break; // Unknown type
                }
            } else {
                break;
            }

            currentStart = nextStart;
            // Update End based on duration
            currentEnd = new Date(currentStart.getTime() + duration);
        }
    });

    return expandedEvents;
};
